import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import axios from 'axios'
import { useAuth } from '../../context/AuthContext'
import { useOnboarding, onboardingStorage } from '../../services/onboardingService'
import OnboardingModal from './OnboardingModal'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)
const IcEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
)
const IcExternal = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
)
const IcCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IcShield = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)
const IcZap = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const IcSpark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/>
  </svg>
)
const IcArrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
)
const IcMessage = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const IcTrendUp = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
)
const IcStorage = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
)
const IcClipboard = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
  </svg>
)
const IcEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)

// ── SoftStat chip ─────────────────────────────────────────────────────────────
function SoftStat({ label, value, icon }) {
  return (
    <div className="soft-stat">
      <span className="soft-stat-icon">{icon}</span>
      <div>
        <div className="soft-stat-value">{value}</div>
        <div className="soft-stat-label">{label}</div>
      </div>
    </div>
  )
}

// ── Usage bar ─────────────────────────────────────────────────────────────────
function UsageBar({ label, used, total, unit = '' }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0
  const cls = pct >= 90 ? 'crit' : pct >= 70 ? 'warn' : ''
  return (
    <div>
      <div className="usage-bar-label">
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace' }}>{used}{unit} / {total}{unit}</span>
      </div>
      <div className="usage-track">
        <div className={`usage-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ── Simple SVG sparkline ──────────────────────────────────────────────────────
function Sparkline({ data }) {
  if (!data || data.length < 2) {
    return <div style={{ height: 80, background: 'var(--border)', borderRadius: 8, opacity: .25 }} />
  }
  const W = 300, H = 80
  const max = Math.max(...data, 1)
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * W},${H - (v / max) * (H - 8)}`)
  const fill = [...pts, `${W},${H}`, `0,${H}`].join(' ')
  return (
    <div className="sparkline-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: 80 }}>
        <defs>
          <linearGradient id="spFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6c63ff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#6c63ff" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={fill} fill="url(#spFill)" />
        <polyline points={pts.join(' ')} fill="none" stroke="#6c63ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

// ── Status badge config ───────────────────────────────────────────────────────
const STATUS_BADGE = {
  pending:     { label: 'Pending',     cls: 'badge-pending' },
  approved:    { label: 'Approved',    cls: 'badge-in_progress' },
  in_progress: { label: 'In Progress', cls: 'badge-in_progress' },
  completed:   { label: 'Done',        cls: 'badge-completed' },
  rejected:    { label: 'Rejected',    cls: 'badge-rejected' },
}

// ── Main component ────────────────────────────────────────────────────────────
const ClientOverview = () => {
  const { user } = useAuth()
  const [aiPrompt, setAiPrompt] = useState('')
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)
  const { shouldShowModal, isFirstLogin } = useOnboarding()

  // ── Preserved API calls ───────────────────────────────────────────────────
  const { data: profile } = useQuery(
    'client-profile',
    () => axios.get('/clients/profile').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: usageData } = useQuery(
    'client-usage',
    () => axios.get('/clients/usage').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: sitesData } = useQuery(
    'client-sites',
    () => axios.get('/sites').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: requestsData } = useQuery(
    'client-requests',
    () => axios.get('/requests?limit=5').then(res => res.data),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  )

  const { data: analyticsData } = useQuery(
    ['client-analytics', '30d'],
    () => axios.get('/analytics/overview?range=30d').then(r => r.data),
    { staleTime: 120000 }
  )

  // Onboarding modal logic (preserved)
  useEffect(() => {
    if (user?.role === 'client' && shouldShowModal && shouldShowModal() && !onboardingStorage.isSkippedForSession()) {
      const shouldShow = isFirstLogin && (isFirstLogin() || !onboardingStorage.wasModalSeen())
      if (shouldShow) {
        setShowOnboardingModal(true)
        onboardingStorage.markModalSeen && onboardingStorage.markModalSeen()
      }
    }
  }, [user, shouldShowModal, isFirstLogin])

  // ── Derived values ────────────────────────────────────────────────────────
  const sites       = sitesData?.sites || (Array.isArray(sitesData) ? sitesData : [])
  const requests    = Array.isArray(requestsData) ? requestsData : (requestsData?.requests || [])
  const firstSite   = sites.length > 0 ? sites[0] : null
  const domain      = firstSite?.domain || profile?.company_name || 'Your site'
  const isLive      = firstSite?.status === 'published' || firstSite?.status === 'live'
  const siteName    = profile?.company_name || firstSite?.name || 'Your site'

  const totalPageViews = analyticsData?.totalPageViews || 0
  const uniqueVisitors = analyticsData?.uniqueVisitors  || 0
  const activityData   = analyticsData?.activityData    || []
  const sparklineNums  = activityData.map(d => d.views || d.count || 0)

  const editsUsed  = profile?.edits_used    || 0
  const editQuota  = profile?.edit_quota    || 50
  const storageMb  = profile?.storage_used_mb || 0
  const siteCount  = sites.length
  const openReqs   = requests.filter(r => ['pending', 'approved', 'in_progress'].includes(r.status)).length

  const onboardingSteps = [
    { label: 'Connect your domain',  done: !!firstSite?.domain,                        to: '/client/domains' },
    { label: 'Customize your site',  done: firstSite?.status === 'published',           to: '/client/editor'  },
    { label: 'Upload your logo',     done: !!profile?.logo_url,                         to: '/client/brand'   },
    { label: 'Add your first page',  done: siteCount > 0,                               to: '/client/sites'   },
    { label: 'Set up email forwarding', done: false,                                    to: '/client/domains' },
    { label: 'Connect analytics',    done: false,                                       to: '/client/analytics' },
  ]
  const onboardingDone  = onboardingSteps.filter(s => s.done).length
  const showOnboarding  = onboardingDone < onboardingSteps.length

  const recentRequests = requests.slice(0, 3)
  const suggestions = [
    'Write a holiday banner for my homepage',
    'Why is my menu page slow?',
    'Add a testimonial section',
  ]

  const firstName = (user?.name || '').trim().split(' ')[0]

  return (
    <div className="page-body" style={{ gap: 18, paddingBottom: 40 }}>

      {/* ── Greeting header (matches prototype) ─────────────────────────── */}
      <div className="page-header" style={{ padding: '8px 0 0' }}>
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Hey{firstName ? ` ${firstName}` : ''} 👋</h1>
        </div>
      </div>

      {/* ── Hero banner ─────────────────────────────────────────────────── */}
      <div className="client-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span className={`status-dot ${isLive ? 'live' : 'draft'}`} />
          <span style={{ fontSize: 13, opacity: .85 }}>{isLive ? 'Site is live' : 'Draft mode'}</span>
          <span style={{ opacity: .5 }}>·</span>
          <span style={{ fontSize: 13, opacity: .65 }}>{domain}</span>
          {firstSite?.domain && (
            <a
              href={`https://${firstSite.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'white', opacity: .65, display: 'inline-flex', alignItems: 'center' }}
            >
              <IcExternal />
            </a>
          )}
        </div>
        <div className="client-hero-site">{siteName}</div>
        <div className="client-hero-domain" style={{ marginBottom: 4 }}>
          {uniqueVisitors > 0
            ? `${uniqueVisitors.toLocaleString()} visitors this month`
            : 'Welcome to your dashboard'}
        </div>
        <div className="client-hero-actions">
          <Link to="/client/editor" style={{ textDecoration: 'none' }}>
            <button className="ds-btn" style={{ background: 'rgba(255,255,255,.18)', borderColor: 'rgba(255,255,255,.3)', color: 'white' }}>
              <IcEdit /> Edit site
            </button>
          </Link>
          {firstSite?.domain && (
            <a href={`https://${firstSite.domain}`} target="_blank" rel="noopener noreferrer">
              <button className="ds-btn" style={{ background: 'rgba(255,255,255,.1)', borderColor: 'rgba(255,255,255,.2)', color: 'white' }}>
                <IcGlobe /> Open site
              </button>
            </a>
          )}
        </div>
        <div style={{ position: 'absolute', right: -60, top: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.07)', pointerEvents: 'none', filter: 'blur(50px)' }} />
      </div>

      {/* ── Stat chips ──────────────────────────────────────────────────── */}
      <div className="soft-stats-row">
        <SoftStat label="Visitors this month" value={uniqueVisitors > 0 ? uniqueVisitors.toLocaleString() : '—'} icon={<IcTrendUp />} />
        <SoftStat label="Page views"          value={totalPageViews > 0 ? totalPageViews.toLocaleString() : '—'} icon={<IcEye />} />
        <SoftStat label="Storage used"        value={storageMb > 0 ? `${storageMb} MB` : '0 MB'} icon={<IcStorage />} />
        <SoftStat label="Active requests"     value={openReqs} icon={<IcClipboard />} />
      </div>

      {/* ── Two-column main ─────────────────────────────────────────────── */}
      <div className="client-bento-2">

        {/* Recent requests */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Recent Requests</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Typical first reply in under 4 hours</div>
            </div>
            <Link to="/client/requests/new" style={{ textDecoration: 'none' }}>
              <button className="ds-btn ds-btn-primary ds-btn-sm">+ New</button>
            </Link>
          </div>

          {recentRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-faint)' }}>
              <IcMessage />
              <div style={{ marginTop: 10, fontSize: 13 }}>No requests yet</div>
              <Link to="/client/requests/new" style={{ textDecoration: 'none' }}>
                <button className="ds-btn ds-btn-primary ds-btn-sm" style={{ marginTop: 12 }}>Create your first request</button>
              </Link>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentRequests.map(req => {
                const b = STATUS_BADGE[req.status] || STATUS_BADGE.pending
                return (
                  <Link key={req.id} to={`/client/requests?open=${req.id}`} style={{ textDecoration: 'none' }}>
                    <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', transition: 'box-shadow .12s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        <span className={`request-badge ${b.cls}`}>{b.label}</span>
                        <span style={{ fontSize: 10.5, color: 'var(--text-faint)', marginLeft: 'auto', fontFamily: 'monospace' }}>
                          {req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {req.title}
                      </div>
                    </div>
                  </Link>
                )
              })}
              <Link to="/client/requests" style={{ fontSize: 12, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, textDecoration: 'none' }}>
                View all requests <IcArrow />
              </Link>
            </div>
          )}
        </div>

        {/* Visitor chart */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Visitors</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Last 30 days</div>
            </div>
            <Link to="/client/analytics" style={{ fontSize: 12, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
              Full analytics <IcArrow />
            </Link>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em', marginBottom: 14 }}>
            {uniqueVisitors > 0 ? uniqueVisitors.toLocaleString() : '—'}
          </div>
          <Sparkline data={sparklineNums} />
        </div>
      </div>

      {/* ── Bottom row: Domain + Plan + AI Helper ───────────────────────── */}
      <div className="client-bento-3">

        {/* Domain card */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Domain & SSL</h2>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,.12)', padding: '2px 8px', borderRadius: 20 }}>
              <IcShield /> Secure
            </span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4, wordBreak: 'break-all' }}>
            {firstSite?.domain || domain}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 14 }}>Auto-renews annually</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'DNS records', ok: true },
              { label: 'SSL certificate', ok: true },
              { label: 'Email forwarding', ok: false },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: item.ok ? 'rgba(34,197,94,.15)' : 'rgba(245,158,11,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.ok ? '#22c55e' : '#f59e0b', flexShrink: 0 }}>
                  {item.ok ? <IcCheck /> : <span style={{ fontSize: 9, fontWeight: 700 }}>!</span>}
                </span>
                <span style={{ flex: 1, color: 'var(--text-muted)' }}>{item.label}</span>
              </div>
            ))}
          </div>
          <Link to="/client/domains" style={{ display: 'block', marginTop: 14, textDecoration: 'none' }}>
            <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ width: '100%', justifyContent: 'center' }}>Manage domain</button>
          </Link>
        </div>

        {/* Plan card */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Your Plan</h2>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'rgba(108,99,255,.12)', padding: '2px 8px', borderRadius: 20 }}>
              <IcZap /> {(profile?.subscription_plan || 'basic').toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            <UsageBar label="Edits this month" used={editsUsed} total={editQuota} />
            <UsageBar label="Storage" used={Math.round(storageMb)} total={2048} unit=" MB" />
            <UsageBar label="Sites" used={siteCount} total={profile?.site_limit || 3} />
          </div>
          <Link to="/client/billing" style={{ display: 'block', marginTop: 14, textDecoration: 'none' }}>
            <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ width: '100%', justifyContent: 'center' }}>Manage plan</button>
          </Link>
        </div>

        {/* AI Helper card */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(108,99,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
              <IcSpark />
            </span>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Webby</h2>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'rgba(108,99,255,.12)', padding: '1px 6px', borderRadius: 20, letterSpacing: '.04em' }}>BETA</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
            Your AI assistant. Ask anything about your website.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {suggestions.map((s, i) => (
              <button key={i} className="ai-chip" onClick={() => setAiPrompt(s)}>
                <span className="ai-chip-arrow">›</span> {s}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-0)' }}>
            <IcSpark />
            <input
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder="Ask Webby anything…"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12.5, fontFamily: 'inherit' }}
            />
          </div>
        </div>
      </div>

      {/* ── Onboarding checklist ─────────────────────────────────────────── */}
      {showOnboarding && (
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Get the most out of WebNest</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                {onboardingDone} of {onboardingSteps.length} complete · Finish setup to unlock everything
              </div>
            </div>
            <div style={{ width: 100 }}>
              <div className="usage-track">
                <div className="usage-fill" style={{ width: `${(onboardingDone / onboardingSteps.length) * 100}%` }} />
              </div>
            </div>
          </div>
          <div className="onboarding-checklist">
            {onboardingSteps.map((step, i) => (
              <Link key={i} to={step.to} className={`onboarding-item${step.done ? ' done' : ''}`} style={{ textDecoration: 'none' }}>
                <span className={`onboarding-check${step.done ? ' done' : ''}`}>
                  {step.done && <IcCheck />}
                </span>
                <span className="onboarding-label">{step.label}</span>
                {!step.done && <IcArrow />}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Onboarding modal (preserved) */}
      {showOnboardingModal && (
        <OnboardingModal
          onComplete={() => {
            setShowOnboardingModal(false)
            onboardingStorage.setSkippedForSession && onboardingStorage.setSkippedForSession(false)
          }}
          onClose={() => {
            setShowOnboardingModal(false)
            if (isFirstLogin && !isFirstLogin()) {
              onboardingStorage.setSkippedForSession && onboardingStorage.setSkippedForSession(true)
            }
          }}
        />
      )}
    </div>
  )
}

export default ClientOverview
